import { isEditorialLocale } from '@/routing/editorial';
import { RentalListLocale, jwtvLpLocales } from '@/routing/lists';
import type { WebLocale } from '@/enums/web-locale';
import { isSportSupported } from '@/features/sports/types/enums';

export type NavItem = 'watch' | 'sports' | 'guide';
export type NavLocation = 'header' | 'settings';
export type NavVisibility = { mobile: boolean; desktop: boolean };

/**
 * Pure function to determine navigation item visibility based on locale features.
 *
 * @param webLocale - Current web locale
 * @param navItem - Navigation item to check ('watch' | 'sports' | 'guide')
 * @param location - Location to check visibility for ('header' | 'settings')
 * @returns Object with mobile and desktop visibility flags
 *
 * @example
 * ```typescript
 * getNavItemVisibility('au', 'sports', 'header')   // { mobile: false, desktop: false }
 * getNavItemVisibility('au', 'sports', 'settings') // { mobile: true, desktop: true }
 * getNavItemVisibility('us', 'guide', 'header')    // { mobile: true, desktop: true }
 * ```
 */
export function getNavItemVisibility(webLocale: WebLocale, navItem: NavItem, location: NavLocation): NavVisibility {
	const hasSports = isSportSupported(webLocale);
	const hasJWTV = jwtvLpLocales.includes(webLocale as RentalListLocale);
	const isGuideLocale = isEditorialLocale(webLocale);

	// Watch navigation item
	if (navItem === 'watch') {
		if (location === 'header') {
			// Watch shows in header when JWTV is available
			return { mobile: hasJWTV, desktop: hasJWTV };
		}
		// Watch never shows in settings
		return { mobile: false, desktop: false };
	}

	// Sports navigation item
	if (navItem === 'sports') {
		if (location === 'header') {
			// Sports shows in header only when JWTV is NOT available
			const show = hasSports && !hasJWTV;
			return { mobile: show, desktop: show };
		}
		// Sports shows in settings when JWTV IS available
		const show = hasJWTV && hasSports;
		return { mobile: show, desktop: show };
	}

	// Guide navigation item
	if (navItem === 'guide') {
		if (location === 'header') {
			// Guide is hidden on mobile when both JWTV and Guide locale are available
			const hideOnMobile = hasJWTV && isGuideLocale;
			// Always show on desktop if it's a guide locale
			return { mobile: !hideOnMobile, desktop: isGuideLocale };
		}
		// Guide shows in settings on mobile only when both JWTV and Guide locale are available
		const showOnMobile = hasJWTV && isGuideLocale;
		return { mobile: showOnMobile, desktop: false };
	}

	// Default: hide everywhere
	return { mobile: false, desktop: false };
}
